import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import HeaderBar from "./HeaderBar";
import SectionHeader from "./SectionHeader";
import CourseCard from "./CourseCard";
import CourseListView from "./CourseListView";
import FooterBar from "./FooterBar";
import QRCodeImg from "../img/Classroom_QR.png";
import {
  DARK_BG,
  EAIM_GREEN,
  EAIM_ORANGE,
  SOFT_GREY,
  TEXT_DARK,
} from "./utils/constants";
import useClassroomFeed from "./hooks/useClassroomFeed";

function useMeasuredCols({ minCard = 260, gap = 20, maxCols = 12 } = {}) {
  const ref = useRef(null);
  const [cols, setCols] = useState(4);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const calc = () => {
      const w = el.clientWidth || window.innerWidth;
      const possible = Math.max(1, Math.floor((w + gap) / (minCard + gap)));
      setCols(Math.min(possible, maxCols));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    window.addEventListener("orientationchange", calc);
    window.addEventListener("resize", calc);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", calc);
      window.removeEventListener("resize", calc);
    };
  }, [minCard, gap, maxCols]);

  return { ref, cols };
}

export default function ClassroomDisplay({ headerBackground }) {
  const DEFAULT_POLL_MS = 30000;
  const DEFAULT_ROTATE_MS = 15000;

  const [pollMs, setPollMs] = useState(DEFAULT_POLL_MS);
  const [rotateMs, setRotateMs] = useState(DEFAULT_ROTATE_MS);

  const { ref: containerRef, cols } = useMeasuredCols({
    minCard: 260,
    gap: 20,
  });

  const initialTheme =
    document.documentElement.getAttribute("data-theme") ||
    localStorage.getItem("eaimTheme") ||
    "light";
  const [theme, setTheme] = useState(initialTheme);
  useEffect(() => {
    const onTheme = (e) => setTheme(e.detail);
    window.addEventListener("eaim-theme", onTheme);
    return () => window.removeEventListener("eaim-theme", onTheme);
  }, []);

  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("eaimViewMode") || "grid"
  );
  useEffect(() => {
    const onViewMode = (e) => setViewMode(e.detail);
    window.addEventListener("eaim-view-mode", onViewMode);
    return () => window.removeEventListener("eaim-view-mode", onViewMode);
  }, []);

  const Background = theme === "dark" ? DARK_BG : SOFT_GREY;
  const Text_color = theme === "dark" ? "#fff" : "#7A8A97";
  const ErrText = theme === "dark" ? "#fff" : "#7A8A97";

  // NOTE: we now also grab `upcoming` and `ongoing` (full lists)
  const {
    loading,
    error,
    lastUpdated,
    refresh,
    upcoming,          // <--- full list of upcoming classes for today
    ongoing,           // <--- full list of ongoing classes for today
    visibleUpcoming,   // paginated slice for grid cards
    visibleOngoing,    // paginated slice for grid cards
    carouselPage,
  } = useClassroomFeed({
    pollMs,
    upcomingWindowMin: 30,
    cols,
    upcomingRows: 1,
    ongoingRows: 1,
    rotateMs,
  });

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API}/getDisplayConfig`,
          {
            headers: { Accept: "application/json" },
          }
        );
        const text = await res.text();
        const json = (res.headers.get("content-type") || "").includes(
          "application/json"
        )
          ? JSON.parse(text)
          : null;
        if (!res.ok || !json?.success)
          throw new Error(json?.message || text || `HTTP ${res.status}`);
        if (abort) return;
        const sec = Number(json.data?.refreshRateSec);
        const rotate = Number(json.data?.rotateMs);
        if (Number.isFinite(sec) && sec > 0) setPollMs(sec * 1000);
        if (Number.isFinite(rotate) && rotate > 0) setRotateMs(rotate);
      } catch {}
    })();
    return () => {
      abort = true;
    };
  }, []);

  const gridWrap = {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 20,
  };

  // map helpers
  function mapUpcoming(arr) {
    return (arr || []).map((c) => ({
      room: c.room,
      title: c.title,
      start: c.time,
      statusText: c.statusText,
      accent: EAIM_GREEN,
    }));
  }
  function mapOngoing(arr) {
    return (arr || []).map((c) => ({
      room: c.room,
      title: c.title,
      end: c.time,
      statusText: c.statusText,
      accent: EAIM_ORANGE,
    }));
  }

  // FULL LISTS for list mode (not paginated)
  const listUpcoming = useMemo(() => mapUpcoming(upcoming), [upcoming]);
  const listOngoing = useMemo(() => mapOngoing(ongoing), [ongoing]);

  return (
    <div
      style={{
        fontFamily:
          "Manrope-Regular, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        background: Background,
        minHeight: "100vh",
        color: TEXT_DARK,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderBar backgroundImg={headerBackground} marqueeSpeed={20} />

      <main
        ref={containerRef}
        style={{
          padding: "20px 24px 26px",
          maxWidth: 2400,
          margin: "0 auto",
          width: "100%",
          flex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          {loading ? (
            <span style={{ fontSize: 14, opacity: 0.7, paddingLeft: "50px" }}>
              Loading schedules…
            </span>
          ) : error ? (
            <span
              style={{ fontSize: 14, color: "#b00020", paddingLeft: "50px" }}
            >
              Failed to load: {error}{" "}
              <button onClick={refresh} style={{ marginLeft: 8 }}>
                Retry
              </button>
            </span>
          ) : (
            <span
              style={{
                fontSize: 12,
                opacity: 0.6,
                color: Text_color,
                paddingLeft: "50px",
              }}
            >
              Updated {lastUpdated ? lastUpdated.toLocaleTimeString() : ""}
            </span>
          )}
        </div>

        {viewMode === "list" ? (
          // LIST VIEW: use FULL arrays -> CourseListView handles paging/count
          <CourseListView
            startingSoon={listUpcoming}
            endingSoon={listOngoing}
            showHeaders={true}
          />
        ) : (
          // GRID VIEW (unchanged): use visibleUpcoming / visibleOngoing (sliced)
          <>
            {/* Upcoming */}
            <SectionHeader text="Upcoming" color={EAIM_GREEN} />
            <AnimatePresence mode="wait">
              <motion.div
                key={`up-${carouselPage}-grid`}
                style={gridWrap}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                {visibleUpcoming.length === 0 && !loading && (
                  <div
                    style={{
                      color: ErrText,
                      fontSize: 14,
                      marginTop: 8,
                      paddingLeft: "50px",
                    }}
                  >
                    No classes starting soon.
                  </div>
                )}
                {visibleUpcoming.map((c, i) => (
                  <CourseCard
                    key={`u-${c.room}-${i}`}
                    room={c.room}
                    time={c.time}
                    title={c.title}
                    statusText={c.statusText}
                    accent={EAIM_GREEN}
                  />
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Ongoing */}
            <SectionHeader text="Ongoing" color={EAIM_ORANGE} />
            <AnimatePresence mode="wait">
              <motion.div
                key={`on-${carouselPage}-grid`}
                style={gridWrap}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                {visibleOngoing.length === 0 && !loading && (
                  <div
                    style={{
                      color: ErrText,
                      fontSize: 14,
                      marginTop: 8,
                      paddingLeft: "50px",
                    }}
                  >
                    No ongoing classes.
                  </div>
                )}
                {visibleOngoing.map((c, i) => (
                  <CourseCard
                    key={`o-${c.room}-${i}`}
                    room={c.room}
                    time={c.time}
                    title={c.title}
                    statusText={c.statusText}
                    accent={EAIM_ORANGE}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </main>

      <FooterBar qrSrc={QRCodeImg} />
    </div>
  );
}
